<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { PRODUCT_TYPE } from "@/util/constants";
import { InfoCircleIcon, XCloseIcon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UIButton,
  UICard,
  UICheckbox,
  UIDivider,
  UIInput,
  UISwitch,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const props = defineProps({
  builderType: {
    type: String,
    default: PRODUCT_TYPE.FORM,
  },
});
const store = useAppStore();

const emailNotificationsConfig = JSON.parse(JSON.stringify(store.emailNotificationsConfig));
const emails = ref(emailNotificationsConfig?.emailTo || []);
const emailInput = ref("");
const subject = ref(emailNotificationsConfig?.subject || store.builder.name);
const replyToEmail = ref(emailNotificationsConfig?.replyToEmail || "");
const senderName = ref(emailNotificationsConfig?.senderName || "");
const showEmailError = ref(false);
const autoResponderConfig = JSON.parse(JSON.stringify(store.autoResponderConfig));
const autoResponderSubject = ref(autoResponderConfig?.subject || store.builder.name);
const autoResponderReplyToEmail = ref(autoResponderConfig?.replyToEmail || "");
const autoResponderSenderName = ref(autoResponderConfig?.senderName || "");

/** Matches main `BuilderEmailSidebar`: API uses `sendSubmissionsAsPdf` for form/survey; quiz uses quiz-specific keys on both configs. */
const readSendSubmissionsAsPdf = (cfg: Record<string, unknown> | null | undefined): boolean =>
  Boolean(cfg?.sendSubmissionsAsPdf ?? cfg?.sendFormSubmissionsAsPdf);

// PDF — Email Notification
const emailNotificationSendSubmissionsAsPdf = ref(readSendSubmissionsAsPdf(emailNotificationsConfig));
const emailNotificationSendQuizSubmissionsAsPdf = ref(
  emailNotificationsConfig?.sendQuizSubmissionsAsPdf || false
);
const emailNotificationSendQuizResultsAsPdf = ref(emailNotificationsConfig?.sendQuizResultsAsPdf || false);

// PDF — Auto Responder
const autoResponderSendSubmissionsAsPdf = ref(readSendSubmissionsAsPdf(autoResponderConfig));
const autoResponderSendQuizSubmissionsAsPdf = ref(autoResponderConfig?.sendQuizSubmissionsAsPdf || false);
const autoResponderSendQuizResultsAsPdf = ref(autoResponderConfig?.sendQuizResultsAsPdf || false);

const isQuiz = computed(() => Boolean(store.quizId));

const addEmail = () => {
  const email = emailInput.value.trim();
  if (email && !emails.value.includes(email)) {
    emails.value.push(email);
  }
  emailInput.value = "";
};

const removeEmail = (index: number) => {
  emails.value.splice(index, 1);
};

const builderTypeConvert = computed(() => {
  if (props.builderType === PRODUCT_TYPE.FORM) {
    return t("form.formNotification");
  } else if (props.builderType === PRODUCT_TYPE.SURVEY) {
    return t("survey.surveyNotfication");
  }
  return t("quiz.quizNotification");
});

const emailTooltipKey = computed(() => {
  if (props.builderType === PRODUCT_TYPE.FORM) {
    return "common.emailTooltipForm";
  } else if (props.builderType === PRODUCT_TYPE.SURVEY) {
    return "common.emailTooltipSurvey";
  }
  return "common.emailTooltipQuiz";
});

const toggleEmailNotifications = (value: boolean) => {
  store.builderConfig.emailNotifications = value;
  store.anyUnsavedChanges = true;
};

const clearEmailFields = () => {
  emails.value = [];
  emailInput.value = "";
  subject.value = store.builder.name;
  replyToEmail.value = "";
  senderName.value = "";
  emailNotificationSendQuizSubmissionsAsPdf.value = false;
  emailNotificationSendQuizResultsAsPdf.value = false;
  emailNotificationSendSubmissionsAsPdf.value = false;
  store.anyUnsavedChanges = true;
};

const saveEmailFields = () => {
  if (emails.value.length === 0) {
    showEmailError.value = true;
  } else {
    showEmailError.value = false;
    const emailPayload = {
      senderName: senderName.value,
      subject: subject.value,
      emailTo: emails.value,
      replyToEmail: replyToEmail.value,
    };
    if (isQuiz.value) {
      store.emailNotificationsConfig = {
        ...emailPayload,
        sendQuizSubmissionsAsPdf: emailNotificationSendQuizSubmissionsAsPdf.value,
        sendQuizResultsAsPdf: emailNotificationSendQuizResultsAsPdf.value,
      };
    } else {
      store.emailNotificationsConfig = {
        ...emailPayload,
        sendSubmissionsAsPdf: emailNotificationSendSubmissionsAsPdf.value,
      };
    }
    store.anyUnsavedChanges = true;
  }
};

const clearAutoResponderFields = () => {
  autoResponderSubject.value = store.builder.name;
  autoResponderReplyToEmail.value = "";
  autoResponderSenderName.value = "";
  autoResponderSendSubmissionsAsPdf.value = false;
  autoResponderSendQuizSubmissionsAsPdf.value = false;
  autoResponderSendQuizResultsAsPdf.value = false;
  store.anyUnsavedChanges = true;
};

const saveAutoResponderFields = () => {
  const autoPayload = {
    subject: autoResponderSubject.value,
    replyToEmail: autoResponderReplyToEmail.value,
    senderName: autoResponderSenderName.value,
  };
  if (isQuiz.value) {
    store.autoResponderConfig = {
      ...autoPayload,
      sendQuizSubmissionsAsPdf: autoResponderSendQuizSubmissionsAsPdf.value,
      sendQuizResultsAsPdf: autoResponderSendQuizResultsAsPdf.value,
    };
  } else {
    store.autoResponderConfig = {
      ...autoPayload,
      sendSubmissionsAsPdf: autoResponderSendSubmissionsAsPdf.value,
    };
  }
  store.anyUnsavedChanges = true;
};

const toggleAutoResponder = (value: boolean) => {
  store.builderConfig.autoResponder = value;
  store.anyUnsavedChanges = true;
};

const autoResponderSubheader = computed(() => {
  if (store.formId) {
    return t("common.autoResponderSubheader");
  }
  if (store.surveyId) {
    return t("survey.autoResponderSubheader");
  }
  return t("quiz.autoResponderSubheader");
});

const sendSubmissionsAsPdfLabel = computed(() => {
  if (store.formId) {
    return t("form.sendSubmissionsAsPdf");
  }
  if (store.surveyId) {
    return t("survey.sendSubmissionsAsPdf");
  }
  return t("quiz.sendSubmissionsAsPdf");
});

// Get default subject placeholders
const emailSubjectPlaceholder = computed(() => {
  return "{form_title} - {name}";
});

const autoResponderSubjectPlaceholder = computed(() => {
  return "We have received your response for {form.title} on {submit.date}";
});
</script>

<template>
  <div class="notifications-view-container h-full overflow-y-auto bg-gray-50 p-6">
    <div class="mx-auto max-w-4xl space-y-6">

      <!-- Email Notification Card -->
      <UICard>
        <div class="space-y-6">
        <div class="flex items-start gap-4">
          <UISwitch
            id="notifications-switch-email-notification"
            :value="store.builderConfig.emailNotifications"
            class="mt-1 flex-shrink-0"
            @update:value="toggleEmailNotifications"
          />
          <div>
            <p class="font-medium text-gray-700">
              {{ t("common.emailNotification") }}
            </p>
            <p class="mt-1 text-sm text-gray-500">
              {{ t("common.emailNotificationSubheader") }}
            </p>
          </div>
        </div>

        <UIDivider v-if="store.builderConfig.emailNotifications" />

        <div v-if="store.builderConfig.emailNotifications" class="space-y-6">
          <div>
            <label for="email-subject" class="mb-1.5 block text-xs font-medium text-gray-700">
              {{ t("common.subject") }}
            </label>
            <UIInput
              id="email-subject"
              v-model="subject"
              type="textarea"
              :placeholder="emailSubjectPlaceholder"
              :autosize="{
                minRows: 3,
                maxRows: 3,
              }"
            />
          </div>

          <div>
            <label for="email-to-input" class="mb-1.5 block text-xs font-medium text-gray-700">
              {{ t("common.emailTo") }}<span class="ml-1 text-red-600">*</span>
              <UITooltip class="max-w-[280px]" trigger="hover" placement="top">
                <template #trigger>
                  <InfoCircleIcon class="ml-1 inline h-3 w-3 text-gray-400"></InfoCircleIcon>
                </template>
                {{ t(emailTooltipKey) }}
              </UITooltip>
            </label>
            <div
              class="flex max-h-52 flex-wrap overflow-y-auto rounded-lg border border-gray-300 px-3.5 py-2.5"
            >
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="(email, index) in emails"
                  :key="index"
                  class="flex items-center gap-1.5 rounded-md border border-gray-300 bg-gray-50 px-2 py-0.5 text-sm font-medium text-gray-700"
                >
                  {{ email }}
                  <UIButton id="remove-email" :quaternary="true" text @click="removeEmail(index)">
                    <XCloseIcon class="h-3 w-3 text-gray-400" />
                  </UIButton>
                </span>
              </div>
              <input
                id="email-to-input"
                v-model="emailInput"
                type="text"
                class="flex-1 border-none bg-transparent p-1.5 text-sm font-medium outline-none"
                @keydown.enter="addEmail"
                @blur="addEmail"
              />
            </div>
            <span v-if="showEmailError" class="mt-1 text-xs text-red-600">{{
              t("common.emailError")
            }}</span>
          </div>

          <div>
            <label for="email-reply-to" class="mb-1.5 block text-xs font-medium text-gray-700">
              {{ t("common.replyToEmail")
              }}<UITooltip class="max-w-[280px]" trigger="hover" placement="top">
                <template #trigger>
                  <InfoCircleIcon class="ml-1 inline h-3 w-3 text-gray-400"></InfoCircleIcon>
                </template>
                {{ t("common.replyToTooltip") }}
              </UITooltip>
            </label>
            <UIInput id="email-reply-to" v-model="replyToEmail" placeholder="" />
          </div>

          <div>
            <label for="email-sender" class="mb-1.5 block text-xs font-medium text-gray-700">
              {{ t("common.senderName") }}
            </label>
            <UIInput id="email-sender" v-model="senderName" placeholder="" />
            <p class="mt-1.5 text-xs text-gray-600">
              {{ t("common.senderNameWarning") }}
            </p>
          </div>

          <!-- PDF — form/survey (matches main BuilderEmailSidebar emailNotificationsConfig.sendSubmissionsAsPdf) -->
          <div v-if="!isQuiz" class="flex items-center">
            <UICheckbox
              id="email-notification-send-submissions-as-pdf"
              v-model:checked="emailNotificationSendSubmissionsAsPdf"
              :label="sendSubmissionsAsPdfLabel"
            />
          </div>

          <!-- PDF — quiz email notification -->
          <div v-if="isQuiz" class="space-y-3">
            <div class="flex items-center">
              <UICheckbox
                id="email-notification-send-quiz-submissions-as-pdf"
                v-model:checked="emailNotificationSendQuizSubmissionsAsPdf"
                :label="t('quiz.sendSubmissionsAsPdf')"
              />
            </div>
            <div class="flex items-center">
              <UICheckbox
                id="email-notification-send-quiz-results-as-pdf"
                v-model:checked="emailNotificationSendQuizResultsAsPdf"
                :label="t('quiz.sendQuizResultsAsPdf')"
              />
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex justify-end gap-3 border-t pt-4">
            <UIButton id="cancel-email-settings" @click="clearEmailFields">
              {{ t("common.cancel") }}
            </UIButton>
            <UIButton id="save-email-settings" type="primary" @click="saveEmailFields">
              {{ t("common.save") }}
            </UIButton>
          </div>
        </div>
        </div>
      </UICard>

      <!-- Auto Responder Card -->
      <UICard>
        <div class="space-y-6">
        <div class="flex items-start gap-4">
          <UISwitch
            id="notifications-switch-auto-responder"
            :value="store.builderConfig.autoResponder"
            class="mt-1 flex-shrink-0"
            @update:value="toggleAutoResponder"
          />
          <div>
            <p class="font-medium text-gray-700">
              {{ t("common.autoResponder") }}
            </p>
            <p class="mt-1 text-sm text-gray-500">
              {{ autoResponderSubheader }}
            </p>
          </div>
        </div>

        <UIDivider v-if="store.builderConfig.autoResponder" />

        <div v-if="store.builderConfig.autoResponder" class="space-y-6">
          <div>
            <label for="auto-responder-subject" class="mb-1.5 block text-xs font-medium text-gray-700">
              {{ t("common.subject") }}
            </label>
            <UIInput
              id="auto-responder-subject"
              v-model="autoResponderSubject"
              type="textarea"
              :placeholder="autoResponderSubjectPlaceholder"
              :autosize="{
                minRows: 3,
                maxRows: 3,
              }"
            />
          </div>

          <div>
            <label for="auto-responder-reply-to" class="mb-1.5 block text-xs font-medium text-gray-700">
              {{ t("common.replyToEmail")
              }}<UITooltip class="max-w-[280px]" trigger="hover" placement="top">
                <template #trigger>
                  <InfoCircleIcon class="ml-1 inline h-3 w-3 text-gray-400"></InfoCircleIcon>
                </template>
                {{ t("common.replyToTooltip") }}
              </UITooltip>
            </label>
            <UIInput
              id="auto-responder-reply-to"
              v-model="autoResponderReplyToEmail"
              placeholder=""
            />
          </div>

          <div>
            <label for="auto-responder-sender" class="mb-1.5 block text-xs font-medium text-gray-700">
              {{ t("common.senderName") }}
            </label>
            <UIInput id="auto-responder-sender" v-model="autoResponderSenderName" placeholder="" />
            <p class="mt-1.5 text-xs text-gray-600">
              {{ t("common.senderNameWarning") }}
            </p>
          </div>

          <!-- PDF — form/survey auto responder -->
          <div v-if="!isQuiz" class="flex items-center">
            <UICheckbox
              id="auto-responder-send-submissions-as-pdf"
              v-model:checked="autoResponderSendSubmissionsAsPdf"
              :label="sendSubmissionsAsPdfLabel"
            />
          </div>

          <!-- PDF — quiz auto responder (main BuilderEmailSidebar parity) -->
          <div v-if="isQuiz" class="space-y-3">
            <div class="flex items-center">
              <UICheckbox
                id="auto-responder-send-quiz-submissions-as-pdf"
                v-model:checked="autoResponderSendQuizSubmissionsAsPdf"
                :label="t('quiz.sendSubmissionsAsPdf')"
              />
            </div>
            <div class="flex items-center">
              <UICheckbox
                id="auto-responder-send-quiz-results-as-pdf"
                v-model:checked="autoResponderSendQuizResultsAsPdf"
                :label="t('quiz.sendQuizResultsAsPdf')"
              />
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex justify-end gap-3 border-t pt-4">
            <UIButton id="cancel-auto-responder-settings" @click="clearAutoResponderFields">
              {{ t("common.cancel") }}
            </UIButton>
            <UIButton
              id="save-auto-responder-settings"
              type="primary"
              @click="saveAutoResponderFields"
            >
              {{ t("common.save") }}
            </UIButton>
          </div>
        </div>
        </div>
      </UICard>
    </div>
  </div>
</template>

<style scoped>
.notifications-view-container {
  height: calc(100vh - 150px);
}

.notifications-view-container p {
  margin-bottom: 0;
}
</style>
